import React, { useState, useEffect, useMemo } from "react";
import {
  Users,
  Plus,
  Edit,
  AlertCircle,
  Search,
  X,
  CreditCard,
  Settings,
  LogOut,
  User,
  Layout,
  Clock,
  MapPin,
  CheckCircle,
  Sparkles,
} from "lucide-react";
import { RestaurantTable, Order, Staff } from "../types";
import { supabase } from "../lib/supabase";
import FloorplanEditor from "./FloorplanEditor";
import FloorplanView from "./FloorplanView";

interface TableManagementProps {
  currentStaff: Staff;
  setCurrentView: (view: "pos" | "payments" | "tables") => void;
  onLogout: () => void;
}

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function money(n: number) {
  return `£${Number(n || 0).toFixed(2)}`;
}

function badgeForStatus(status: string) {
  switch (status) {
    case "available":
      return "bg-emerald-50 text-emerald-800 ring-emerald-200";
    case "occupied":
      return "bg-rose-50 text-rose-800 ring-rose-200";
    case "reserved":
      return "bg-amber-50 text-amber-900 ring-amber-200";
    case "cleaning":
      return "bg-slate-100 text-slate-700 ring-slate-200";
    default:
      return "bg-slate-100 text-slate-700 ring-slate-200";
  }
}

function dotForStatus(status: string) {
  switch (status) {
    case "available":
      return "bg-emerald-500";
    case "occupied":
      return "bg-rose-500";
    case "reserved":
      return "bg-amber-500";
    case "cleaning":
      return "bg-slate-500";
    default:
      return "bg-slate-500";
  }
}

const TableManagement: React.FC<TableManagementProps> = ({ currentStaff, setCurrentView, onLogout }) => {
  const [tables, setTables] = useState<RestaurantTable[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedTable, setSelectedTable] = useState<RestaurantTable | null>(null);
  const [showAddTable, setShowAddTable] = useState(false);
  const [showEditTable, setShowEditTable] = useState(false);
  const [showFloorplan, setShowFloorplan] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "floorplan">("grid");
  const [searchTerm, setSearchTerm] = useState("");
  const [newTable, setNewTable] = useState({
    table_number: "",
    seats: 4,
    x_position: 0,
    y_position: 0,
  });

  useEffect(() => {
    fetchTables();
    fetchOrders();

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const hasRealDatabase = supabaseUrl && supabaseUrl !== "https://demo.supabase.co";

    let tablesSubscription: any = null;
    let ordersSubscription: any = null;

    if (hasRealDatabase) {
      tablesSubscription = supabase
        .channel("tables")
        .on("postgres_changes", { event: "*", schema: "public", table: "restaurant_tables" }, () => {
          fetchTables();
        })
        .subscribe();

      ordersSubscription = supabase
        .channel("orders")
        .on("postgres_changes", { event: "*", schema: "public", table: "orders" }, () => {
          fetchOrders();
        })
        .subscribe();
    }

    return () => {
      if (tablesSubscription) tablesSubscription.unsubscribe();
      if (ordersSubscription) ordersSubscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTables = async () => {
    try {
      const { data, error } = await supabase.from("restaurant_tables").select("*").order("table_number");

      if (error) {
        console.error("Error fetching tables:", error);
        setTables([
          { id: "1", table_number: 1, seats: 4, status: "available", x_position: 100, y_position: 100, created_at: "" } as any,
          { id: "2", table_number: 2, seats: 2, status: "occupied", x_position: 150, y_position: 150, created_at: "" } as any,
          { id: "3", table_number: 3, seats: 4, status: "available", x_position: 100, y_position: 200, created_at: "" } as any,
          { id: "4", table_number: 4, seats: 6, status: "reserved", x_position: 200, y_position: 200, created_at: "" } as any,
          { id: "5", table_number: 5, seats: 2, status: "available", x_position: 300, y_position: 100, created_at: "" } as any,
          { id: "6", table_number: 6, seats: 8, status: "cleaning", x_position: 400, y_position: 200, created_at: "" } as any,
        ]);
      } else {
        setTables((data || []) as any);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from("orders")
        .select(
          `
          *,
          order_items!order_items_order_id_fkey (
            *,
            menu_items!order_items_menu_item_id_fkey (*)
          )
        `
        )
        .neq("status", "completed")
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Error fetching orders:", error);
        setOrders([]);
      } else {
        const transformedOrders = (data as any[])?.map((order) => ({
          ...order,
          order_items:
            order.order_items?.map((item: any) => ({
              ...item,
              menu_item: item.menu_items,
            })) || [],
        }));
        setOrders(transformedOrders as any);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const addTable = async () => {
    if (!newTable.table_number) return;

    try {
      const { error } = await supabase
        .from("restaurant_tables")
        .insert([
          {
            table_number: parseInt(newTable.table_number),
            seats: newTable.seats,
            status: "available",
            x_position: newTable.x_position,
            y_position: newTable.y_position,
          },
        ]);

      if (error) {
        console.error("Error adding table:", error);
        alert("Error adding table");
      } else {
        setShowAddTable(false);
        setNewTable({ table_number: "", seats: 4, x_position: 0, y_position: 0 });
        fetchTables();
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Table added successfully (demo mode)");
      setShowAddTable(false);
      setNewTable({ table_number: "", seats: 4, x_position: 0, y_position: 0 });
    }
  };

  const updateTableStatus = async (tableId: string, status: string) => {
    setTables(
      tables.map((table) => (table.id === tableId ? ({ ...table, status: status as any } as any) : table))
    );
    setSelectedTable(null);

    try {
      const { error } = await supabase.from("restaurant_tables").update({ status }).eq("id", tableId);

      if (error) {
        console.error("Error updating table:", error);
        fetchTables();
      }
    } catch (error) {
      console.error("Error:", error);
      fetchTables();
    }
  };

  const transferTable = async (fromTableId: string, toTableNumber: number) => {
    const order = orders.find((o: any) => o.table_number === (tables as any).find((t: any) => t.id === fromTableId)?.table_number);
    if (!order) return;

    try {
      const { error } = await supabase.from("orders").update({ table_number: toTableNumber }).eq("id", (order as any).id);

      if (error) {
        console.error("Error transferring table:", error);
      }

      fetchOrders();
      alert(`Order transferred to table ${toTableNumber}`);
    } catch (error) {
      console.error("Error:", error);
      alert(`Order transferred to table ${toTableNumber} (demo mode)`);
    }
  };

  const getTableOrder = (tableNumber: number) => {
    return (orders as any[]).find((order) => order.table_number === tableNumber);
  };

  const filteredTables = useMemo(() => {
    const t = searchTerm.trim();
    if (!t) return tables;
    return tables.filter((table) => table.table_number.toString().includes(t));
  }, [tables, searchTerm]);

  const stats = useMemo(() => {
    const by = { available: 0, occupied: 0, reserved: 0, cleaning: 0 } as any;
    for (const table of tables as any[]) {
      const order = getTableOrder(table.table_number);
      const actualStatus = order ? "occupied" : table.status;
      by[actualStatus] = (by[actualStatus] || 0) + 1;
    }
    const total = tables.length;
    return { total, ...by };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables, orders]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-2xl bg-slate-900 text-white flex items-center justify-center font-bold shrink-0">
                F
              </div>
              <div className="min-w-0">
                <div className="text-base sm:text-lg font-semibold tracking-tight truncate">Forkcast POS</div>
                <div className="text-xs text-slate-500 truncate">Tables • Management</div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Mode tabs */}
              <div className="hidden md:flex items-center gap-2 rounded-2xl bg-slate-100 p-1 ring-1 ring-slate-200">
                <button
                  onClick={() => setCurrentView("pos")}
                  className="px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 hover:text-slate-900 transition"
                >
                  POS
                </button>

                <button
                  onClick={() => setCurrentView("payments")}
                  className="px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 hover:text-slate-900 transition inline-flex items-center gap-2"
                >
                  <CreditCard size={16} />
                  Payments
                </button>

                <button
                  onClick={() => setCurrentView("tables")}
                  className="px-3 py-2 rounded-xl text-sm font-semibold bg-white shadow-sm ring-1 ring-slate-200 inline-flex items-center gap-2"
                >
                  <Settings size={16} />
                  Tables
                </button>
              </div>

              {/* Staff chip */}
              <div className="hidden lg:flex items-center gap-2 rounded-2xl bg-white ring-1 ring-slate-200 px-3 py-2">
                <User size={16} className="text-slate-500" />
                <div className="text-xs leading-tight">
                  <div className="font-semibold">{currentStaff.name}</div>
                  <div className="text-slate-500 capitalize">{currentStaff.role}</div>
                </div>
              </div>

              <button
                onClick={onLogout}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold bg-rose-600 text-white hover:bg-rose-700 active:bg-rose-800 transition shadow-sm"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>

          {/* Search + controls row */}
          <div className="mt-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
            {/* Search */}
            <div className="flex items-center gap-2 rounded-2xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2.5 w-full lg:max-w-md">
              <Search size={18} className="text-slate-400" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search table #…"
                className="w-full bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
              />
              {searchTerm ? (
                <button
                  onClick={() => setSearchTerm("")}
                  className="rounded-xl px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition"
                >
                  Clear
                </button>
              ) : null}
            </div>

            {/* View mode + add */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end">
              <div className="flex items-center rounded-2xl bg-slate-100 p-1 ring-1 ring-slate-200">
                <button
                  onClick={() => setViewMode("grid")}
                  className={cx(
                    "px-4 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center gap-2",
                    viewMode === "grid" ? "bg-white ring-1 ring-slate-200 shadow-sm" : "text-slate-700 hover:text-slate-900"
                  )}
                >
                  <Settings size={16} />
                  Grid
                </button>
                <button
                  onClick={() => setViewMode("floorplan")}
                  className={cx(
                    "px-4 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center gap-2",
                    viewMode === "floorplan"
                      ? "bg-white ring-1 ring-slate-200 shadow-sm"
                      : "text-slate-700 hover:text-slate-900"
                  )}
                >
                  <Layout size={16} />
                  Floorplan
                </button>
              </div>

              <button
                onClick={() => setShowAddTable(true)}
                className="rounded-2xl px-5 py-2.5 bg-slate-900 text-white font-bold hover:bg-slate-800 active:bg-slate-950 transition shadow-sm inline-flex items-center justify-center gap-2"
              >
                <Plus size={18} />
                Add Table
              </button>
            </div>
          </div>

          {/* Stats + legend */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-3">
              <div className="text-xs text-slate-500 font-semibold">Total</div>
              <div className="text-xl font-extrabold">{stats.total}</div>
            </div>
            <div className="rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 p-3">
              <div className="text-xs text-emerald-800 font-semibold">Available</div>
              <div className="text-xl font-extrabold text-emerald-800">{stats.available}</div>
            </div>
            <div className="rounded-2xl bg-rose-50 ring-1 ring-rose-200 p-3">
              <div className="text-xs text-rose-800 font-semibold">Occupied</div>
              <div className="text-xl font-extrabold text-rose-800">{stats.occupied}</div>
            </div>
            <div className="rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-3">
              <div className="text-xs text-amber-900 font-semibold">Reserved</div>
              <div className="text-xl font-extrabold text-amber-900">{stats.reserved}</div>
            </div>
            <div className="rounded-2xl bg-slate-100 ring-1 ring-slate-200 p-3">
              <div className="text-xs text-slate-700 font-semibold">Cleaning</div>
              <div className="text-xl font-extrabold text-slate-700">{stats.cleaning}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {viewMode === "grid" ? (
        <div className="px-4 sm:px-6 py-6">
          {filteredTables.length === 0 ? (
            <div className="min-h-[55vh] flex items-center justify-center">
              <div className="text-center">
                <div className="mx-auto h-16 w-16 rounded-2xl bg-slate-100 ring-1 ring-slate-200 flex items-center justify-center">
                  <AlertCircle size={28} className="text-slate-500" />
                </div>
                <div className="mt-4 text-lg font-semibold">
                  {searchTerm ? "No tables match your search" : "No tables found"}
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  {searchTerm ? "Try another number." : "Add a table to get started."}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filteredTables.map((table: any) => {
                const order = getTableOrder(table.table_number);
                const actualStatus = order ? "occupied" : table.status;

                return (
                  <button
                    key={table.id}
                    onClick={() => setSelectedTable(table)}
                    className="text-left rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm hover:shadow-md transition overflow-hidden"
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xs text-slate-500 font-semibold">Table</div>
                          <div className="text-3xl font-extrabold tracking-tight text-slate-900">
                            {table.table_number}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <span
                            className={cx(
                              "inline-flex items-center gap-2 text-xs font-semibold rounded-xl px-3 py-1 ring-1",
                              badgeForStatus(actualStatus)
                            )}
                          >
                            <span className={cx("h-2 w-2 rounded-full", dotForStatus(actualStatus))} />
                            {actualStatus}
                          </span>

                          <span
                            className="rounded-xl p-2 ring-1 ring-slate-200 hover:bg-slate-50 transition"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTable(table);
                              setShowEditTable(true);
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            <Edit size={16} className="text-slate-700" />
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between">
                        <div className="inline-flex items-center gap-2 text-sm text-slate-700 font-semibold">
                          <Users size={16} className="text-slate-500" />
                          {table.seats} seats
                        </div>

                        {order ? (
                          <div className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700">
                            <Clock size={14} className="text-slate-500" />
                            Active order
                          </div>
                        ) : (
                          <div className="text-xs text-slate-500">—</div>
                        )}
                      </div>

                      {order ? (
                        <div className="mt-3 rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-3">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold text-slate-900">#{(order as any).order_number}</div>
                            <div className="text-xs font-semibold text-slate-600 capitalize">{(order as any).status}</div>
                          </div>
                          {(order as any).customer_name ? (
                            <div className="text-xs text-slate-500 truncate mt-1">{(order as any).customer_name}</div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="px-4 sm:px-6 py-6 min-h-0">
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm overflow-hidden">
            <FloorplanView
              currentStaff={currentStaff}
              tables={tables}
              orders={orders}
              onTableSelect={(table: any) => setSelectedTable(table)}
              onEditFloorplan={() => setShowFloorplan(true)}
              onTableStatusUpdate={(tableId: string, status: string) => {
                setTables(
                  tables.map((table: any) => (table.id === tableId ? ({ ...table, status: status as any } as any) : table))
                );
                updateTableStatus(tableId, status);
              }}
            />
          </div>
        </div>
      )}

      {/* Floorplan Editor */}
      {showFloorplan && (
        <FloorplanEditor
          currentStaff={currentStaff}
          tables={tables}
          onClose={() => setShowFloorplan(false)}
          onTablesUpdate={(updatedTables: any) => {
            setTables(updatedTables);
            localStorage.setItem("demo_tables", JSON.stringify(updatedTables));
          }}
        />
      )}

      {/* Table Details Modal */}
      {selectedTable && !showEditTable && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelectedTable(null)} />
          <div className="relative h-full w-full flex items-center justify-center p-4">
            <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden">
              <div className="p-5 border-b border-slate-200 bg-white">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm text-slate-500">Table</div>
                    <div className="text-2xl font-extrabold tracking-tight">#{(selectedTable as any).table_number}</div>
                  </div>
                  <button
                    onClick={() => setSelectedTable(null)}
                    className="rounded-xl p-2 ring-1 ring-slate-200 hover:bg-slate-50 active:bg-slate-100 transition"
                  >
                    <X size={20} className="text-slate-700" />
                  </button>
                </div>

                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <span className={cx("inline-flex items-center gap-2 text-xs font-semibold rounded-xl px-3 py-1 ring-1", badgeForStatus((selectedTable as any).status))}>
                    <span className={cx("h-2 w-2 rounded-full", dotForStatus((selectedTable as any).status))} />
                    {(selectedTable as any).status}
                  </span>
                  <span className="inline-flex items-center gap-2 text-xs font-semibold rounded-xl px-3 py-1 ring-1 ring-slate-200 bg-slate-50 text-slate-700">
                    <Users size={14} className="text-slate-500" />
                    {(selectedTable as any).seats} seats
                  </span>
                </div>
              </div>

              <div className="p-5 space-y-5">
                {/* Current Order */}
                {(() => {
                  const order: any = getTableOrder((selectedTable as any).table_number);
                  return order ? (
                    <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">
                            {order.status === "completed" ? "Recent Order (Customers Still Seated)" : "Current Order"}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">Order #{order.order_number}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-extrabold text-emerald-700">{money(order.total_amount)}</div>
                          <div className="text-xs text-slate-600 capitalize">{order.status}</div>
                        </div>
                      </div>

                      {order.customer_name ? (
                        <div className="mt-3 inline-flex items-center gap-2 text-sm text-slate-700 font-semibold">
                          <User size={16} className="text-slate-500" />
                          <span className="truncate">{order.customer_name}</span>
                        </div>
                      ) : null}

                      {order.status === "completed" ? (
                        <div className="mt-4 rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-3 text-sm text-amber-900 font-semibold">
                          Mark table as available when customers leave.
                        </div>
                      ) : null}
                    </div>
                  ) : null;
                })()}

                {/* Status Actions */}
                <div>
                  <div className="text-sm font-semibold text-slate-900 mb-3">Change Status</div>
                  <div className="grid grid-cols-2 gap-2">
                    {["available", "occupied", "reserved", "cleaning"].map((status) => {
                      const order: any = getTableOrder((selectedTable as any).table_number);
                      const hasUnpaidOrder = order && order.status === "completed" && order.payment_status === "pending";
                      const isActive = (selectedTable as any).status === status;

                      return (
                        <button
                          key={status}
                          onClick={() => {
                            updateTableStatus((selectedTable as any).id, status);
                            if (status === "available" && hasUnpaidOrder) {
                              alert("Table marked as available. Note: There is still an unpaid order for this table.");
                            }
                          }}
                          disabled={isActive}
                          className={cx(
                            "rounded-2xl px-4 py-3 font-bold capitalize transition ring-1",
                            isActive
                              ? "bg-slate-100 text-slate-400 ring-slate-200 cursor-not-allowed"
                              : status === "available" && hasUnpaidOrder
                              ? "bg-amber-600 text-white ring-amber-600 hover:bg-amber-700"
                              : "bg-slate-900 text-white ring-slate-900 hover:bg-slate-800 active:bg-slate-950"
                          )}
                        >
                          {status === "available" && hasUnpaidOrder ? "⚠ Available" : status}
                        </button>
                      );
                    })}
                  </div>

                  {(() => {
                    const order: any = getTableOrder((selectedTable as any).table_number);
                    const hasUnpaidOrder = order && order.status === "completed" && order.payment_status === "pending";
                    return hasUnpaidOrder ? (
                      <div className="mt-3 rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-3 text-xs text-amber-900">
                        ⚠ This table has an unpaid order. Marking as available will allow new customers to be seated.
                      </div>
                    ) : null;
                  })()}
                </div>

                {/* Table Transfer */}
                {getTableOrder((selectedTable as any).table_number) ? (
                  <div>
                    <div className="text-sm font-semibold text-slate-900 mb-3">Transfer Order</div>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          transferTable((selectedTable as any).id, parseInt(e.target.value));
                          setSelectedTable(null);
                        }
                      }}
                      className="w-full rounded-2xl px-3 py-3 bg-white ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400/50"
                      defaultValue=""
                    >
                      <option value="">Select table...</option>
                      {(tables as any[])
                        .filter((t) => t.table_number !== (selectedTable as any).table_number && t.status === "available")
                        .map((t) => (
                          <option key={t.id} value={t.table_number}>
                            Table {t.table_number} ({t.seats} seats)
                          </option>
                        ))}
                    </select>
                  </div>
                ) : null}
              </div>

              <div className="p-4 border-t border-slate-200 bg-white flex items-center justify-end gap-2">
                <button
                  onClick={() => setSelectedTable(null)}
                  className="rounded-xl px-4 py-2 font-semibold bg-white ring-1 ring-slate-200 hover:bg-slate-50 active:bg-slate-100 transition"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowEditTable(true);
                  }}
                  className="rounded-xl px-4 py-2 font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-950 transition"
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Table Modal */}
      {showAddTable && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAddTable(false)} />
          <div className="relative h-full w-full flex items-center justify-center p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden">
              <div className="p-5 border-b border-slate-200 bg-white">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm text-slate-500">Tables</div>
                    <div className="text-2xl font-extrabold tracking-tight">Add New Table</div>
                  </div>
                  <button
                    onClick={() => setShowAddTable(false)}
                    className="rounded-xl p-2 ring-1 ring-slate-200 hover:bg-slate-50 active:bg-slate-100 transition"
                  >
                    <X size={20} className="text-slate-700" />
                  </button>
                </div>
              </div>

              <div className="p-5 space-y-4">
                <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-4">
                  <label className="block text-xs font-semibold text-slate-600 mb-2">Table Number</label>
                  <input
                    type="number"
                    value={newTable.table_number}
                    onChange={(e) => setNewTable({ ...newTable, table_number: e.target.value })}
                    className="w-full rounded-xl px-3 py-3 bg-white ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400/50"
                    placeholder="Enter table number"
                  />
                </div>

                <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-4">
                  <label className="block text-xs font-semibold text-slate-600 mb-2">Number of Seats</label>
                  <input
                    type="number"
                    value={newTable.seats}
                    onChange={(e) => setNewTable({ ...newTable, seats: parseInt(e.target.value) || 4 })}
                    className="w-full rounded-xl px-3 py-3 bg-white ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400/50"
                    min="1"
                    max="20"
                  />
                </div>

                <button
                  onClick={addTable}
                  disabled={!newTable.table_number}
                  className={cx(
                    "w-full rounded-2xl py-3 font-bold inline-flex items-center justify-center gap-2 transition shadow-sm",
                    !newTable.table_number ? "bg-slate-200 text-slate-500 cursor-not-allowed" : "bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-950"
                  )}
                >
                  <Sparkles size={18} />
                  Add Table
                </button>
              </div>

              <div className="p-4 border-t border-slate-200 bg-white flex items-center justify-end">
                <button
                  onClick={() => setShowAddTable(false)}
                  className="rounded-xl px-4 py-2 font-semibold bg-white ring-1 ring-slate-200 hover:bg-slate-50 active:bg-slate-100 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableManagement;
